import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";
import PublicLayout from "@/components/layout/PublicLayout";
import { ACCENT, visibleModules } from "@/modules/registry";
import { ArrowRight, Sparkles } from "lucide-react";

const ModuleCard = ({ m, t }) => {
  const Icon = m.icon;
  const accent = ACCENT[m.accentClass] || ACCENT.emerald;
  const isActive = m.status === "active";
  const title = t(`apps.${m.key}.title`);
  const tagline = t(`apps.${m.key}.tagline`);
  // Hub-Visibility-v1 (Racket Lab brief, Feb 2026) — some modules ship
  // with a detailed bullet list of capabilities under the tagline.
  // i18n delivers them as a single `\n`-joined string so the existing
  // string-only `t()` resolver keeps working; we split here. Missing
  // key → empty array → renders only the tagline paragraph.
  const pointsRaw = t(`apps.${m.key}.taglinePoints`);
  const points =
    pointsRaw && pointsRaw !== `apps.${m.key}.taglinePoints`
      ? pointsRaw.split("\n").map((s) => s.trim()).filter(Boolean)
      : [];
  return (
    <article
      className={`relative rounded-2xl border ${accent.border} ${accent.bg} p-5 flex flex-col gap-3 transition hover:shadow-md`}
      data-testid={`apps-card-${m.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`w-11 h-11 rounded-xl bg-white/80 border ${accent.border} flex items-center justify-center ${accent.text}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span
          className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-full border ${
            isActive
              ? "border-emerald-300 bg-emerald-100 text-emerald-800"
              : "border-slate-200 bg-white/80 text-slate-500"
          }`}
          data-testid={`apps-card-${m.key}-status`}
        >
          {isActive ? t("apps.statusActive") : t("apps.statusComingSoon")}
        </span>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        {points.length > 0 ? (
          <div className="mt-2" data-testid={`apps-card-${m.key}-bullets`}>
            {/* Optional lead-in line ("Manage your rackets:"). When the
                i18n string is missing we keep the bullets stand-alone. */}
            {(() => {
              const lead = t(`apps.${m.key}.taglineLead`);
              if (!lead || lead === `apps.${m.key}.taglineLead`) return null;
              return (
                <p
                  className="text-sm font-medium text-slate-700 mb-1.5"
                  data-testid={`apps-card-${m.key}-lead`}
                >
                  {lead}
                </p>
              );
            })()}
            <ul
              className="text-sm text-slate-600 leading-relaxed space-y-0.5"
              data-testid={`apps-card-${m.key}-points`}
            >
              {points.map((p, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-slate-400 shrink-0 select-none">-</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{tagline}</p>
        )}
      </div>
      <div className="mt-auto pt-2">
        {isActive ? (
          <Link
            to={m.activeRoute}
            className={`inline-flex items-center gap-1.5 text-sm font-medium text-white ${accent.btn} px-3.5 py-2 rounded-lg`}
            data-testid={`apps-card-${m.key}-open`}
          >
            {t("apps.openApp")} <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
          </Link>
        ) : (
          <Link
            to={m.route}
            className={`inline-flex items-center gap-1.5 text-sm font-medium ${accent.text} bg-white border ${accent.border} px-3.5 py-2 rounded-lg hover:bg-slate-50`}
            data-testid={`apps-card-${m.key}-learn`}
          >
            {t("apps.learnMore")} <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
          </Link>
        )}
      </div>
    </article>
  );
};

const AppsHub = () => {
  const { t } = useLang();
  return (
    <PublicLayout>
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 py-10 sm:py-14" data-testid="apps-hub-page">
          <div className="mb-10 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
              <Sparkles className="w-3 h-3" /> {t("apps.heroBadge")}
            </span>
            {/* Hub-Visibility-v1 (Hero copy update, Feb 2026) — same
                visual weight as before (semibold, h1, tight tracking,
                slate-900), but each sentence on its own line. Splitting
                on \n keeps i18n a single string per language; CSS
                handles the wrap. */}
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 mt-3"
              data-testid="apps-hero-title"
            >
              {t("apps.heroTitle").split("\n").map((line, i) => (
                <span key={i} className="block">{line}</span>
              ))}
            </h1>
            <p className="text-base text-slate-600 mt-3 leading-relaxed" data-testid="apps-hero-subtitle">
              {t("apps.heroSubtitle")}
            </p>
            {/* HUB-PRINCIPLE-CARD (Feb 2026) — wrap the closing
                inspirational triplet in a soft emerald card that
                mirrors the visual language of the landing's
                "Gana recompensas" badge (rounded-2xl, emerald-100
                border, emerald-50→emerald-100/50 gradient). Four
                subtle corner accents anchor it as a "premium tag"
                without adding visual noise. Typography is kept
                verbatim (italic, slate, centred, leading-relaxed). */}
            <div
              className="relative mx-auto mt-5 max-w-xl"
              data-testid="apps-hero-principle-wrap"
            >
              <span
                aria-hidden
                className="pointer-events-none absolute -top-px -left-px w-3 h-3 border-t-2 border-l-2 border-emerald-400/70 rounded-tl-2xl"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute -top-px -right-px w-3 h-3 border-t-2 border-r-2 border-emerald-400/70 rounded-tr-2xl"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute -bottom-px -left-px w-3 h-3 border-b-2 border-l-2 border-emerald-400/70 rounded-bl-2xl"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute -bottom-px -right-px w-3 h-3 border-b-2 border-r-2 border-emerald-400/70 rounded-br-2xl"
              />
              <div
                className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-emerald-100/50 px-5 py-3.5 sm:px-6 sm:py-4 text-sm text-slate-600 italic leading-relaxed text-center"
                data-testid="apps-hero-principle"
              >
                {t("apps.heroPrinciple").split("\n").map((line, i) => (
                  <span key={i} className="block">{line}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="apps-hub-grid">
            {visibleModules().map((m) => <ModuleCard key={m.key} m={m} t={t} />)}
          </div>

          <div className="mt-12 border-t border-slate-200 pt-6 text-xs text-slate-500" data-testid="apps-hub-footer">
            {t("apps.footerNote")}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default AppsHub;
